import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import useSchool, { BRAND_COLOR_FALLBACK } from '@/hooks/useSchool';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  ArrowLeft, Plus, RefreshCw, FileText, CheckCircle2, Clock, Users, ListChecks,
} from 'lucide-react';

const STATUS_LABELS = {
  draft: { label: 'Draft', color: 'bg-amber-100 text-amber-700 border-amber-300' },
  published: { label: 'Live', color: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  closed: { label: 'Closed', color: 'bg-slate-100 text-slate-600 border-slate-300' },
};

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return iso; }
}

export default function SchoolTeacherAssessments() {
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [classes, setClasses] = useState([]);
  const [classFilter, setClassFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);
  const school = useSchool(me?.school_id);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (classFilter !== 'all') params.school_class_id = classFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await api.get('/teacher/me/assessments', { params });
      setAssessments(res.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load assessments');
      setAssessments([]);
    } finally {
      setLoading(false);
    }
  }, [classFilter, statusFilter]);

  useEffect(() => {
    (async () => {
      try {
        const meRes = await api.get('/auth/me');
        setMe(meRes.data);
        const ts = await api.get('/teacher/me/assignments', {
          params: { status: 'active' },
        });
        const seen = new Map();
        for (const t of ts.data || []) {
          if (t?.school_class) seen.set(t.school_class.school_class_id, t.school_class);
        }
        setClasses(Array.from(seen.values()));
      } catch {/* guarded */}
    })();
  }, []);

  useEffect(() => { load(); }, [load]);

  const brandColor = school?.brand_color || BRAND_COLOR_FALLBACK;

  const grouped = useMemo(() => {
    const out = { draft: [], published: [], closed: [] };
    for (const a of assessments) {
      const k = a.status || 'draft';
      (out[k] || (out[k] = [])).push(a);
    }
    return out;
  }, [assessments]);

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50"
      data-testid="school-teacher-assessments"
    >
      <header
        className="border-b bg-white/90 backdrop-blur sticky top-0 z-10"
        style={{ borderTopWidth: 4, borderTopColor: brandColor }}
      >
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost" size="sm"
              onClick={() => navigate('/school-teacher')}
              data-testid="back-to-teacher-home-btn"
            >
              <ArrowLeft size={16} className="mr-1.5" /> Back
            </Button>
            <div>
              <h1 className="font-bold text-slate-900">Assessments</h1>
              <p className="text-xs text-slate-500">{me?.email} · Teacher</p>
            </div>
          </div>
          <Button
            onClick={() => navigate('/school-teacher/assessments/new')}
            style={{ backgroundColor: brandColor }}
            className="text-white"
            data-testid="new-assessment-btn"
          >
            <Plus size={16} className="mr-1.5" /> New assessment
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex flex-wrap items-end gap-3 mb-6">
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-500 uppercase">Class</label>
            <Select value={classFilter} onValueChange={setClassFilter}>
              <SelectTrigger className="w-[200px]" data-testid="filter-class">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All classes</SelectItem>
                {classes.map((c) => (
                  <SelectItem key={c.school_class_id} value={c.school_class_id}>
                    {c.name} (Grade {c.grade}{c.section ? `-${c.section}` : ''})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-500 uppercase">Status</label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]" data-testid="filter-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="draft">Drafts</SelectItem>
                <SelectItem value="published">Live</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="outline" size="sm"
            onClick={load} disabled={loading}
            data-testid="refresh-assessments-btn"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="assessments-skeleton">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white border rounded-xl p-5 animate-pulse h-44" />
            ))}
          </div>
        ) : assessments.length === 0 ? (
          <div
            className="border-2 border-dashed border-slate-200 rounded-2xl py-16 px-6 text-center bg-white/60"
            data-testid="assessments-empty-state"
          >
            <div className="mx-auto w-14 h-14 rounded-full bg-indigo-50 flex items-center justify-center mb-4">
              <FileText size={26} className="text-indigo-500" />
            </div>
            <p className="text-slate-900 font-semibold text-lg">No assessments yet</p>
            <p className="text-sm text-slate-600 mt-1 max-w-md mx-auto">
              Create your first assessment to generate RAG-grounded MCQs for your class.
            </p>
            <Button
              onClick={() => navigate('/school-teacher/assessments/new')}
              className="mt-5 text-white"
              style={{ backgroundColor: brandColor }}
              data-testid="empty-create-btn"
            >
              <Plus size={14} className="mr-1.5" /> New assessment
            </Button>
          </div>
        ) : (
          <div className="space-y-8">
            {['draft', 'published', 'closed'].map((status) => (
              grouped[status]?.length > 0 && (
                <section key={status}>
                  <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">
                    {STATUS_LABELS[status].label} · {grouped[status].length}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {grouped[status].map((a) => (
                      <AssessmentCard
                        key={a.assessment_id}
                        assessment={a}
                        brandColor={brandColor}
                      />
                    ))}
                  </div>
                </section>
              )
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function AssessmentCard({ assessment, brandColor }) {
  const navigate = useNavigate();
  const subj = assessment.subject;
  const cls = assessment.school_class;
  const status = STATUS_LABELS[assessment.status] || STATUS_LABELS.draft;
  return (
    <div
      className="bg-white border rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow flex flex-col"
      data-testid={`assessment-card-${assessment.assessment_id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <h4
          className="font-semibold text-slate-900 leading-tight line-clamp-2"
          data-testid={`assessment-title-${assessment.assessment_id}`}
        >
          {assessment.title}
        </h4>
        <Badge variant="outline" className={status.color}>{status.label}</Badge>
      </div>
      <div className="text-xs text-slate-500 space-y-1 mb-3">
        <div>{cls?.name || '—'} · Grade {cls?.grade || '—'}{cls?.section ? `-${cls.section}` : ''}</div>
        {subj && (
          <div style={{ color: subj.color }} className="font-medium">{subj.name}</div>
        )}
        <div className="flex items-center gap-1.5">
          <Clock size={12} /> Due {formatDate(assessment.due_at)}
        </div>
        <div className="flex items-center gap-1.5">
          <ListChecks size={12} /> {assessment.num_questions_requested} Qs · {assessment.release_mode === 'auto' ? 'Auto-release' : 'Hold-back'}
        </div>
        {Array.isArray(assessment.assigned_to_enrollment_ids) && assessment.assigned_to_enrollment_ids.length > 0 && (
          <div
            className="inline-flex items-center gap-1.5 mt-1 px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 text-[10px] font-medium"
            data-testid={`assessment-audience-${assessment.assessment_id}`}
          >
            <Users size={10} />
            {assessment.audience_summary || `${assessment.assigned_to_enrollment_ids.length} students`}
          </div>
        )}
      </div>
      <div className="mt-auto flex flex-col gap-2">
        {assessment.status === 'draft' && (
          <Button
            size="sm"
            onClick={() => navigate(`/school-teacher/assessments/${assessment.assessment_id}`)}
            style={{ backgroundColor: brandColor }}
            className="text-white"
            data-testid={`review-draft-btn-${assessment.assessment_id}`}
          >
            <CheckCircle2 size={14} className="mr-1.5" /> Review & publish
          </Button>
        )}
        {assessment.status !== 'draft' && (
          <>
            <Button
              size="sm" variant="outline"
              onClick={() => navigate(`/school-teacher/assessments/${assessment.assessment_id}/status`)}
              data-testid={`view-status-btn-${assessment.assessment_id}`}
            >
              <Users size={14} className="mr-1.5" /> Student status
            </Button>
            <Button
              size="sm" variant="ghost"
              onClick={() => navigate(`/school-teacher/assessments/${assessment.assessment_id}`)}
              data-testid={`view-questions-btn-${assessment.assessment_id}`}
            >
              View questions
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
